import { api } from "./api";
import type { Receivable, Receipt, BuyerBalance, ApplyCreditResult } from "@/types/models";

interface PaginatedResponse<T> {
  data: T[];
  meta: { total: number; limit: number; offset: number };
}

export const receivableService = {
  list: (params?: Record<string, string | number>, signal?: AbortSignal) =>
    api.get<PaginatedResponse<Receivable>>("/receivables", { params, signal }).then((r) => r.data),

  get: (id: number) =>
    api.get<Receivable>(`/receivables/${id}`).then((r) => r.data),

  create: (data: {
    buyer_id: number;
    sale_type: string;
    sale_id?: number; // none for an opening due
    date: string;
    due_date?: string;
    invoice_number?: string;
    total_amount: number;
    notes?: string;
  }) => api.post<Receivable>("/receivables", data).then((r) => r.data),

  getBalances: () =>
    api.get<BuyerBalance[]>("/receivables/balances").then((r) => r.data),

  listReceipts: (receivableId: number) =>
    api.get<Receipt[]>(`/receivables/${receivableId}/receipts`).then((r) => r.data),

  // Record Receipt: CASH only. Settles the buyer's open invoices oldest-first
  // in one transaction; whatever is left is held as credit. Bank / UPI /
  // cheque receipts come in through a statement upload instead.
  recordBuyerReceipt: (data: { buyer_id: number; date: string; amount: number; notes?: string; local_id?: string }) =>
    api
      .post<{ receipt: Receipt; settled: ApplyCreditResult | null }>("/receivables/buyer-receipts", data)
      .then((r) => r.data),

  // FIFO-apply the buyer's available advance credit across all their open
  // invoices in one action (settlement half of bank-tagged deposits).
  applyCredit: (buyerID: number) =>
    api.post<ApplyCreditResult>("/receivables/apply-credit", { buyer_id: buyerID }).then((r) => r.data),

  update: (id: number, data: {
    buyer_id: number;
    date: string;
    due_date?: string;
    invoice_number?: string;
    total_amount: number;
    notes?: string;
  }) => api.put<Receivable>(`/receivables/${id}`, data).then((r) => r.data),

  updateReceipt: (id: number, data: {
    receivable_id: number;
    date: string;
    amount: number;
    method: string;
    reference?: string;
    notes?: string;
  }) => api.put<Receipt>(`/receivables/receipts/${id}`, data).then((r) => r.data),

  // Only an opening due can be deleted here: a sale's invoice goes with its sale.
  remove: (id: number) => api.delete(`/receivables/${id}`),

  deleteReceipt: (id: number) =>
    api.delete(`/receivables/receipts/${id}`),
};
